import { createContext, useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import { ADMIN_SETTINGS, AUTH_ME, SCHOOL_INFO } from '../../../config/apiPaths';
import { DEFAULT_SCHOOL_SETTINGS } from '../../../config/settingsConfig';
import { type UserAgentInterface, useUserAgent } from '../../../hooks/useUserAgent';
import { localStorageKeys } from '../../../localStorageKeys';
import {
    type AuthMeResponse,
    type GetAdminSettings,
    type SchoolInfo,
    type SchoolSettings
} from '../../../types/apiTypes';
import { getEnvironment } from '../../../utils/environment';
import { getUrls } from '../../../utils/url';

interface DownloadContextInterface {
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    schoolInfo?: SchoolInfo;
    adminSettings?: SchoolSettings;
    userAgent: UserAgentInterface;
    isTeamsSchool: boolean;
    downloadURL: string;
    canDownload: boolean;
    isHipaa: boolean;
}

export type View = 'SessionSummary' | 'StudentDetail';

const DownloadContext = createContext(undefined as unknown as DownloadContextInterface);

const { Provider, Consumer } = DownloadContext;

const DownloadProvider: React.FC = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | undefined>(undefined);
    const [adminSettings, setAdminSettings] = useState<SchoolSettings>(DEFAULT_SCHOOL_SETTINGS);
    const userAgent = useUserAgent(navigator.userAgent);

    const IOS_URL = 'https://itunes.apple.com/app/id1544666621';
    const ANDROID_URL = 'https://play.google.com/store/apps/details?id=com.class.android';

    const { api } = getUrls();

    const downloadURL = useMemo(() => {
        // iOS 13+ has Mac user agent in Safari
        if (userAgent.isIOS || userAgent.isIPad) {
            return IOS_URL;
        }
        if (userAgent.isAndroid || userAgent.isChromebook) {
            return ANDROID_URL;
        }

        // Mac/Windows downloads
        const downloadUrl = new URL(`${api}/api/zoom/download/`);
        downloadUrl.searchParams.append('platform', userAgent.isMac ? 'Mac' : 'Windows');

        return downloadUrl.href;
    }, [userAgent, api]);

    const hasApp = adminSettings?.has_app || false;
    const { environment } = getEnvironment();
    const isHipaa = environment === 'hipaa';
    const isTeamsSchool = schoolInfo?.zoom_app_id === 'teams';

    const canDownload = useMemo(() => {
        if (!hasApp) {
            return false;
        }

        if (isTeamsSchool) {
            return false;
        }

        if (!userAgent.deviceSupported) {
            return false;
        }

        if (isHipaa) {
            return false;
        }

        return true;
    }, [hasApp, isHipaa, isTeamsSchool, userAgent.deviceSupported]);

    useEffect(() => {
        const getSchoolInfo = async () => {
            const { data } = await axios.get<SchoolInfo>(`${api}${SCHOOL_INFO}`);
            setSchoolInfo(data);
        };

        const getAdminSettings = async () => {
            const { data } = await axios.get<GetAdminSettings>(`${api}${ADMIN_SETTINGS}`);
            setAdminSettings(data.data);
        };

        const checkAuth = async () => {
            const token = window.localStorage.getItem(localStorageKeys.userUuid);
            if (!token) {
                return;
            }

            try {
                const { data } = await axios.get<AuthMeResponse>(`${api}${AUTH_ME}`, { headers: { token } });
                if (data && data.user_status === 'active') {
                    window.location.href = '/v1_index_client.html?websdk=true';
                }
            } catch (err) {
                console.error(err);
            }
        };

        Promise.all([getSchoolInfo(), getAdminSettings(), checkAuth()]).then(() => setIsLoading(false));
    }, [api]);

    return (
        <Provider
            value={{
                isLoading,
                setIsLoading,
                schoolInfo,
                adminSettings,
                userAgent,
                downloadURL,
                canDownload,
                isHipaa,
                isTeamsSchool
            }}
        >
            {children}
        </Provider>
    );
};

export { DownloadContext, DownloadProvider, Consumer as DownloadConsumer };
